import { useState } from 'react';
import { BlocoCampos, CampoArea, Interruptor } from '../componentes/campos';
import { FiguraMarcada, proximoNumero } from '../componentes/figura-marcada';
import { ListaEditavel, ListaTextos } from '../componentes/lista-editavel';
import { SlotImagem } from '../componentes/slot-imagem';
import { novaEtapa, novoComando, novoTexto } from '../manual-defaults';
import { TEXTOS } from '../manual-textos';
import type { ItemComando, ItemEtapa } from '../manual-types';
import type { CampoLista } from '../use-manual-editor';
import type { PropsSecao } from './tipos';

const COLUNAS_ETAPA = [
  { chave: 'descricao' as const, rotulo: 'Procedimento', placeholder: 'Descreva a ação na ordem em que deve ser executada', largura: '2fr', tipo: 'area' as const },
  { chave: 'observacao' as const, rotulo: 'Observação', placeholder: 'Cuidado, verificação ou critério de aceite', largura: '1.3fr', tipo: 'area' as const },
];

export function SecaoOperacao({ editor }: PropsSecao) {
  const { dados, definirCampo, definirOpcao, adicionarItem, atualizarItem, removerItem, moverItem } = editor;
  const [pontoAtivo, setPontoAtivo] = useState<string | null>(null);

  const marcadores = dados.comandos
    .filter((item) => typeof item.x === 'number' && typeof item.y === 'number')
    .map((item) => ({ id: item.id, numero: item.identificacao, x: item.x as number, y: item.y as number, lx: item.lx, ly: item.ly }));

  /** Arrasto na foto: nasce um comando já numerado, com a seta naquele lugar. */
  const criarPonto = (posicao: { x: number; y: number; lx?: number; ly?: number }) => {
    const item = novoComando({ identificacao: proximoNumero(dados.comandos.map((comando) => comando.identificacao)), ...posicao });
    adicionarItem('comandos', item);
    setPontoAtivo(item.id);
  };

  const sequencia = (campo: CampoLista, itens: ItemEtapa[], rotulo: string) => (
    <ListaEditavel<ItemEtapa>
      itens={itens}
      colunas={COLUNAS_ETAPA}
      rotuloAdicionar={`Adicionar etapa de ${rotulo}`}
      vazio="Nenhuma etapa cadastrada."
      aoAdicionar={() => adicionarItem(campo, novaEtapa())}
      aoAtualizar={(id, patch) => atualizarItem(campo, id, patch)}
      aoRemover={(id) => removerItem(campo, id)}
      aoMover={(id, direcao) => moverItem(campo, id, direcao)}
    />
  );

  return (
    <>
      <BlocoCampos titulo="Apresentação da operação" descricao="Item 5.1 — abertura do capítulo de operação.">
        <CampoArea
          rotulo="Texto de apresentação"
          linhas={4}
          valor={dados.apresentacaoOperacao}
          placeholder={TEXTOS.operacaoIntro}
          aoMudar={(valor) => definirCampo('apresentacaoOperacao', valor)}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Comandos, indicadores e dispositivos" descricao="Painel, botoeiras, seletores e sinalizadores usados na operação. Envie a foto do painel e clique sobre cada comando: o ponto numerado vira uma linha da tabela.">
        <div className="manual-comandos-figura">
          <SlotImagem
            rotulo="Foto do painel / posto de comando"
            descricao="Clique ou arraste a foto. Depois, pressione sobre cada comando e arraste para o lado para criar a seta numerada."
            imagem={dados.imagemComandos}
            altura={300}
            aoDefinir={(imagem) => definirCampo('imagemComandos', imagem)}
            conteudo={dados.imagemComandos ? (
              <FiguraMarcada
                imagem={dados.imagemComandos}
                alt="Painel de comandos"
                marcadores={marcadores}
                ativo={pontoAtivo}
                alturaMaxima={282}
                aoCriar={criarPonto}
                aoMover={(id, patch) => atualizarItem('comandos', id, patch)}
                aoSelecionar={setPontoAtivo}
              />
            ) : undefined}
          />
          <p className="manual-marcador-dica">
            Pressione sobre o componente na foto e <strong>arraste para o lado</strong>: a seta aponta para ele e o número <strong>1, 2, 3…</strong> fica onde você soltar.
            Um clique sem arrastar cria só o número sobre o ponto. Depois, arraste o número ou a ponta da seta para ajustar; remover a linha da tabela remove a seta.
          </p>
        </div>
        <ListaEditavel<ItemComando>
          itens={dados.comandos}
          colunas={[
            { chave: 'identificacao', rotulo: 'Identificação', placeholder: 'Ex.: S1' },
            { chave: 'tipo', rotulo: 'Tipo', placeholder: 'Ex.: Botão pulsador', largura: '0.9fr' },
            { chave: 'funcao', rotulo: 'Função', placeholder: 'Ex.: Inicia o ciclo de prensagem', largura: '1.6fr' },
            { chave: 'local', rotulo: 'Localização', placeholder: 'Ex.: Painel frontal' },
          ]}
          rotuloAdicionar="Adicionar comando"
          vazio="Nenhum comando cadastrado."
          aoAdicionar={() => adicionarItem('comandos', novoComando())}
          aoAtualizar={(id, patch) => atualizarItem('comandos', id, patch)}
          aoRemover={(id) => removerItem('comandos', id)}
          aoMover={(id, direcao) => moverItem('comandos', id, direcao)}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Preparação (5.2)" descricao="Verificações e ajustes antes de iniciar o ciclo.">
        {sequencia('preparacao', dados.preparacao, 'preparação')}
      </BlocoCampos>

      <BlocoCampos titulo="Operação (5.3)" descricao="Sequência do ciclo normal de trabalho.">
        {sequencia('operacao', dados.operacao, 'operação')}
      </BlocoCampos>

      <BlocoCampos titulo="Parada normal (5.4)" descricao="Encerramento do turno ou interrupção programada.">
        {sequencia('parada', dados.parada, 'parada')}
      </BlocoCampos>

      <BlocoCampos titulo="Emergência (5.5)" descricao="Atuação diante de situação anormal durante a operação.">
        {sequencia('emergenciaEtapas', dados.emergenciaEtapas, 'emergência')}
      </BlocoCampos>

      <BlocoCampos titulo="Mau uso razoavelmente previsível (5.6)" descricao="Comportamentos previsíveis, porém indevidos, que devem ser proibidos explicitamente.">
        <Interruptor
          rotulo="Incluir o item no documento"
          ativo={dados.opcoes.incluirUsoImprevisivel}
          aoMudar={(ativo) => definirOpcao('incluirUsoImprevisivel', ativo)}
        />
        {dados.opcoes.incluirUsoImprevisivel && (
          <ListaTextos
            itens={dados.usosImprevisiveis}
            multilinha
            placeholder="Ex.: Retirar a peça da matriz com a máquina em ciclo."
            rotuloAdicionar="Adicionar mau uso previsível"
            vazio="Nenhum mau uso previsível cadastrado."
            aoAdicionar={() => adicionarItem('usosImprevisiveis', novoTexto())}
            aoAtualizar={(id, patch) => atualizarItem('usosImprevisiveis', id, patch)}
            aoRemover={(id) => removerItem('usosImprevisiveis', id)}
            aoMover={(id, direcao) => moverItem('usosImprevisiveis', id, direcao)}
            sugestoes={[
              'Alcançar a zona de trabalho com a máquina energizada.',
              'Neutralizar sensores ou proteções para agilizar o ciclo.',
              'Operar a máquina a partir de posição não prevista.',
              'Utilizar ferramentas improvisadas para posicionar a peça.',
            ]}
            aoEscolherSugestao={(valor) => adicionarItem('usosImprevisiveis', novoTexto(valor))}
          />
        )}
      </BlocoCampos>
    </>
  );
}
